import React from 'react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, Button, CircularProgress, LinearProgress, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type { BatchSubmitClaimsProgressResponse } from '@store/generated/claims';

import type { SelectionAction } from '../shared';
import { useBatchSuccessAutoDismiss } from '../shared/useBatchSuccessAutoDismiss';

/**
 * THE one action surface for the claims worklist (GDN-3216, Option A):
 * rendered in the DataTable's header slot, it is the contextual header the
 * Polaris/Carbon pattern describes — selecting, submitting, and terminal are
 * STATES of this single component, never a selection bar and a batch tray
 * competing for a bottom edge. The fixed-bottom occlusion class dies here.
 *
 * `batches` is a list on purpose (decision 6): today it carries at most one
 * entry — one batch at a time is the locked model — but the future
 * multi-batch phase changes this component's math, not its callers' shape.
 *
 * Copy and semantics are the batch tray's, verbatim: billers already read
 * these words; the surface moved, the language didn't.
 */

const TERMINAL_STATUSES = new Set(['completed', 'completed_with_failures', 'failed']);
const ATTENTION_STATUSES = new Set(['blocked', 'failed', 'outcome_unknown']);

function batchHeadline(progress: BatchSubmitClaimsProgressResponse): string {
  const status = progress.status ?? 'queued';
  const submitted = progress.submitted ?? 0;
  const attention =
    (progress.blocked ?? 0) + (progress.failed ?? 0) + (progress.outcome_unknown ?? 0);
  if (status === 'completed') return `${submitted} claim${submitted === 1 ? '' : 's'} sent`;
  if (status === 'completed_with_failures') {
    return `${submitted} sent · ${attention} need${attention === 1 ? 's' : ''} attention`;
  }
  if (status === 'failed') return 'Claim submission stopped';
  if (status === 'queued')
    return `Preparing ${progress.total} claim${progress.total === 1 ? '' : 's'}`;
  return `Sending claims · ${progress.processed ?? 0} of ${progress.total}`;
}

export interface ClaimBulkActionSlotProps {
  selectedCount: number;
  /** The selecting state's actions — same contract the SelectionToolbar used. */
  actions: SelectionAction[];
  onClearSelection: () => void;
  /** 0..1 entries today (one batch at a time); shaped as a list for the
   *  future multi-batch phase. */
  batches: BatchSubmitClaimsProgressResponse[];
  onReviewClaim?: (claimId: string) => void;
  onDismissBatch: () => void;
}

export function ClaimBulkActionSlot({
  actions,
  batches,
  onClearSelection,
  onDismissBatch,
  onReviewClaim,
  selectedCount,
}: ClaimBulkActionSlotProps) {
  const theme = useTheme();
  const batch = batches[0];
  /* A fresh selection outranks a FINISHED batch: the row lock released at
     terminal, so new work owns the surface the moment it starts — the
     terminal summary returns if the selection is cleared (the batch stays
     undismissed underneath). A RUNNING batch always wins; selection is
     locked while it drains, so there is nothing fresher to show. */
  const batchIsTerminal = batch != null && TERMINAL_STATUSES.has(batch.status ?? 'queued');
  const showSelecting = selectedCount > 0 && (batch == null || batchIsTerminal);
  /* PURE success — every claim reached the clearinghouse — earns a quiet
     exit after a pause (GDN-3236); any attention outcome persists until
     the biller dismisses it. That asymmetry is the point. */
  const batchAttentionCount = (batch?.items ?? []).filter(item =>
    ATTENTION_STATUSES.has(item.status ?? ''),
  ).length;
  const pureSuccess = !showSelecting && batch?.status === 'completed' && batchAttentionCount === 0;
  const { dismissing, holdProps } = useBatchSuccessAutoDismiss(pureSuccess, onDismissBatch);

  return (
    <Box
      data-testid="claim-bulk-action-slot"
      {...holdProps}
      /* The batch states are announcements — AT hears the completion even
         when the visual auto-dismisses. */
      role={batch && !showSelecting ? 'status' : undefined}
      sx={{
        alignItems: 'center',
        /* Opaque base + tint — the slot sits over scrolling rows when the
           header sticks; a translucent bar would let them bleed through. */
        bgcolor: 'background.paper',
        backgroundImage: `linear-gradient(${alpha(theme.palette.primary.main, 0.06)}, ${alpha(
          theme.palette.primary.main,
          0.06,
        )})`,
        display: 'flex',
        minHeight: 44,
        px: 2.5,
        py: 0.75,
        width: '100%',
        /* CSS owns the exit (house rule); the hook only schedules removal.
           Under reduced motion the hook dismisses instantly instead. */
        opacity: dismissing ? 0 : 1,
        transition: 'opacity 200ms ease',
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
      }}
    >
      {!showSelecting && batch ? (
        <BatchContent batch={batch} onDismiss={onDismissBatch} onReviewClaim={onReviewClaim} />
      ) : (
        <SelectingContent
          actions={actions}
          count={selectedCount}
          onClearSelection={onClearSelection}
        />
      )}
    </Box>
  );
}

function SelectingContent({
  actions,
  count,
  onClearSelection,
}: {
  actions: SelectionAction[];
  count: number;
  onClearSelection: () => void;
}) {
  return (
    <Stack
      direction="row"
      spacing={1.5}
      sx={{ alignItems: 'center', flexWrap: 'wrap', width: '100%' }}
    >
      <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>{count} selected</Typography>
      {actions.map(action => (
        <Button
          key={action.label}
          color={action.color ?? 'primary'}
          disabled={action.disabled || action.loading}
          onClick={action.onClick}
          size="small"
          startIcon={action.loading ? <CircularProgress size={14} /> : undefined}
          sx={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'none' }}
          variant={action.variant ?? 'contained'}
        >
          {action.label}
        </Button>
      ))}
      <Button
        onClick={onClearSelection}
        size="small"
        sx={{ fontSize: '0.75rem', ml: 'auto !important', textTransform: 'none' }}
        variant="text"
      >
        Clear
      </Button>
    </Stack>
  );
}

function BatchContent({
  batch,
  onDismiss,
  onReviewClaim,
}: {
  batch: BatchSubmitClaimsProgressResponse;
  onDismiss: () => void;
  onReviewClaim?: (claimId: string) => void;
}) {
  const status = batch.status ?? 'queued';
  const isTerminal = TERMINAL_STATUSES.has(status);
  const attentionItems = (batch.items ?? []).filter(item =>
    ATTENTION_STATUSES.has(item.status ?? ''),
  );
  const firstAttentionItem = attentionItems[0];
  const processed = Math.min(batch.total, Math.max(0, batch.processed ?? 0));
  const progressPercent = batch.total > 0 ? (processed / batch.total) * 100 : 0;

  return (
    <Stack
      direction="row"
      spacing={2}
      sx={{ alignItems: 'center', flexWrap: 'wrap', width: '100%' }}
    >
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        {isTerminal ? (
          attentionItems.length > 0 ? (
            <ErrorOutlineIcon color="error" fontSize="small" />
          ) : (
            <CheckCircleOutlineIcon color="success" fontSize="small" />
          )
        ) : (
          /* Decorative — the labeled LinearProgress below is the one
             progress indicator screen readers should meet. */
          <CircularProgress aria-hidden size={14} />
        )}
        <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
          {batchHeadline(batch)}
        </Typography>
      </Stack>
      {!isTerminal && (
        <Box sx={{ flex: 1, minWidth: 120 }}>
          <LinearProgress
            aria-label="Claim submission progress"
            aria-valuetext={`${processed} of ${batch.total} claims processed`}
            sx={{
              bgcolor: 'action.disabledBackground',
              borderRadius: 999,
              height: 6,
              '& .MuiLinearProgress-bar': { borderRadius: 999, transition: 'transform 240ms ease' },
              '@media (prefers-reduced-motion: reduce)': {
                '& .MuiLinearProgress-bar': { transition: 'none' },
              },
            }}
            value={progressPercent}
            variant="determinate"
          />
        </Box>
      )}
      <Typography sx={{ color: 'text.secondary', fontSize: '0.75rem' }} variant="caption">
        {isTerminal
          ? attentionItems.length > 0
            ? 'Sent claims are complete; review only the exceptions.'
            : 'Every selected claim reached the clearinghouse.'
          : 'You can keep working while these drain in the background.'}
      </Typography>
      <Stack direction="row" spacing={1} sx={{ ml: 'auto !important' }}>
        {firstAttentionItem?.claim_id && onReviewClaim && (
          <Button
            onClick={() => onReviewClaim(firstAttentionItem.claim_id ?? '')}
            size="small"
            startIcon={<OpenInNewIcon />}
            sx={{ fontSize: '0.75rem', textTransform: 'none' }}
            variant="contained"
          >
            Review {attentionItems.length === 1 ? 'claim' : `${attentionItems.length} claims`}
          </Button>
        )}
        {isTerminal && (
          <Button
            onClick={onDismiss}
            size="small"
            sx={{ fontSize: '0.75rem', textTransform: 'none' }}
            variant="text"
          >
            Dismiss
          </Button>
        )}
      </Stack>
    </Stack>
  );
}
